/* eslint-disable no-magic-numbers */
import { IntlShape } from 'react-intl';
import { getCurrencySymbolFromCode } from '@futrli/localisation';

export const formatCurrency = (value: number, currencyCode: string, compact = false): string => {
  const symbol = getCurrencySymbolFromCode(currencyCode);
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (compact && absValue >= 1000) {
    const k = Math.round(absValue / 1000);
    return `${sign}${symbol}${k.toLocaleString()}k`;
  }

  return `${sign}${symbol}${absValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export const formatPercent = (value: number): string => {
  const pct = value * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

export const formatPercentUnsigned = (value: number): string => `${(value * 100).toFixed(1)}%`;

export const formatRatio = (value: number): string => value.toFixed(2);

export const formatMetricValue = (value: number, format: string, currencyCode: string, compact = false): string => {
  switch (format) {
    case 'percent':
      return formatPercentUnsigned(value);
    case 'currency':
      return formatCurrency(value, currencyCode, compact);
    case 'ratio':
      return formatRatio(value);
    default:
      return String(value);
  }
};

const fallbackLabel = (kpiKey: string): string =>
  kpiKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const getKpiLabel = (kpiKey: string, variant: 'accountant' | 'business_owner', intl: IntlShape): string => {
  if (variant === 'accountant') {
    const accountantLabels: Record<string, string> = {
      net_profit_margin: intl.formatMessage({ id: 'aiInsights.netProfitMargin', defaultMessage: 'Net profit margin' }),
      revenue_growth: intl.formatMessage({ id: 'aiInsights.revenueGrowth', defaultMessage: 'Revenue growth' }),
      net_assets: intl.formatMessage({ id: 'aiInsights.netAssets', defaultMessage: 'Net assets' }),
      gross_margin_ratio: intl.formatMessage({ id: 'aiInsights.grossMargin', defaultMessage: 'Gross margin' }),
      operating_margin_ratio: intl.formatMessage({ id: 'aiInsights.operatingMargin', defaultMessage: 'Operating margin' }),
      bank_accounts_balance: intl.formatMessage({ id: 'aiInsights.cashBalance', defaultMessage: 'Cash balance' }),
      working_capital: intl.formatMessage({ id: 'aiInsights.workingCapital', defaultMessage: 'Working capital' }),
      current_ratio: intl.formatMessage({ id: 'aiInsights.currentRatio', defaultMessage: 'Current ratio' })
    };
    return accountantLabels[kpiKey] ?? fallbackLabel(kpiKey);
  }

  const businessOwnerLabels: Record<string, string> = {
    net_profit_margin: intl.formatMessage({ id: 'aiInsights.kpi.businessOwner.netProfitMargin', defaultMessage: 'What you keep from every sale' }),
    revenue_growth: intl.formatMessage({ id: 'aiInsights.kpi.businessOwner.revenueGrowth', defaultMessage: 'Sales trend' }),
    net_assets: intl.formatMessage({ id: 'aiInsights.kpi.businessOwner.netAssets', defaultMessage: 'Business value' }),
    bank_accounts_balance: intl.formatMessage({ id: 'aiInsights.kpi.businessOwner.cashBalance', defaultMessage: 'Cash in the bank' }),
    working_capital: intl.formatMessage({ id: 'aiInsights.kpi.businessOwner.workingCapital', defaultMessage: 'Working Capital' }),
    current_ratio: intl.formatMessage({ id: 'aiInsights.kpi.businessOwner.currentRatio', defaultMessage: 'Current Ratio' })
  };
  return businessOwnerLabels[kpiKey] ?? fallbackLabel(kpiKey);
};

export const mapStatusToVariant = (status: 'green' | 'yellow' | 'red'): 'positive' | 'warning' | 'negative' => {
  switch (status) {
    case 'green':
      return 'positive';
    case 'yellow':
      return 'warning';
    case 'red':
      return 'negative';
    default:
      return 'warning';
  }
};

export const mapSentimentToVariant = (
  sentiment: 'positive' | 'negative' | 'neutral' | 'warning'
): 'positive' | 'warning' | 'negative' | 'neutral' => {
  switch (sentiment) {
    case 'positive':
      return 'positive';
    case 'negative':
      return 'negative';
    case 'neutral':
      return 'neutral';
    case 'warning':
      return 'warning';
    default:
      return 'neutral';
  }
};

export const mapInsightStatusToAccordionVariant = (status: 'green' | 'yellow' | 'red'): 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'green':
      return 'success';
    case 'red':
      return 'error';
    default:
      return 'warning';
  }
};

export const mapImpactToLabel = (impact: 'high' | 'medium' | 'low', intl: IntlShape): string => {
  switch (impact) {
    case 'high':
      return intl.formatMessage({ id: 'aiInsights.impact.high', defaultMessage: 'High Impact' });
    case 'medium':
      return intl.formatMessage({ id: 'aiInsights.impact.medium', defaultMessage: 'Medium Impact' });
    case 'low':
      return intl.formatMessage({ id: 'aiInsights.impact.low', defaultMessage: 'Low Impact' });
    default:
      return intl.formatMessage({ id: 'aiInsights.impact.medium', defaultMessage: 'Medium Impact' });
  }
};

export const mapImpactToVariant = (
  impact: 'high' | 'medium' | 'low'
): 'negative' | 'warning' | 'neutral' => {
  switch (impact) {
    case 'high':
      return 'negative';
    case 'medium':
      return 'warning';
    case 'low':
      return 'neutral';
    default:
      return 'warning';
  }
};

// Converts API period strings like "FY-2026" → "FY26"
export const formatPeriodLabel = (period: string): string => {
  const fyMatch = /^FY-(\d{4})$/.exec(period);
  if (fyMatch) {
    return `FY${fyMatch[1].slice(-2)}`;
  }
  return period;
};

export const COST_SEGMENT_COLORS = [
  '#004526', // DS dash-light/qualitative/fill-1
  '#1478A9', // DS dash-light/qualitative/fill-2
  '#692238', // DS dash-light/qualitative/fill-3
  '#6B4FA0', // fill-4 placeholder
  '#BA8B00', // fill-5 placeholder
  '#128287'  // fill-6 placeholder
];
